import { duplicateSequence } from './mutations';
import type { PlaybooksGenerationInputs, PlaybooksSaveSequenceStepsResponse, SequenceSteps } from './schemas';

/** The injected throwing `apiFetch` (`_shared/hooks/useApi.ts`), never imported here. */
type ApiFetch = (input: RequestInfo, init?: RequestInit) => Promise<Response>;

/**
 * Plan `2026-09-03-001-launcher-v2-round2-followup-plan.md`, Unit A1 (the
 * Save-vs-Save-as-new-copy gate) — the "Save as new copy" action's shared
 * first half, used by both of the gate's call sites
 * (`SequencesStep.tsx`'s `SequenceEditorShell` and `SequenceCopyPane.tsx`):
 * duplicate the shared Sequence with the caller's in-progress edited `steps`
 * already seeded onto the new identity's v1, in ONE request
 * (`mutations.ts#duplicateSequence(apiFetch, sourceSequenceId, steps)` —
 * `POST /v2/sequences/:id/duplicate` with a `{ steps }` body, round-2-followup
 * plan Unit B2, decision 3). The duplicate keeps the source's `channel` and
 * bound `flowId`, if any.
 *
 * This used to be two separate requests (duplicate, then a separate
 * `saveSequenceSteps` call onto the fresh duplicate's `versionId`) — a shape
 * that could partially fail: the duplicate could succeed with STALE
 * (source-head) content while the edit-save failed separately, leaving an
 * orphaned duplicate visible in the library with the wrong copy on it. Making
 * the seed atomic with the create means that state is no longer
 * representable: the duplicate's v1 IS the edited steps from the moment it
 * exists, or the whole call fails and nothing was created.
 *
 * The `save` field of the return value is synthesized from the single
 * duplicate response (`{ id, versionId, versionNumber }`) plus the `steps`
 * this function sent — never a second network round trip. This is faithful
 * because `POST /v2/sequences/:id/versions` (the endpoint `save` used to come
 * from) echoes the caller's own `steps` back verbatim with no server-side
 * normalization (`api.ts`'s `saveSequenceSteps` handler), and a fresh
 * `createIdentity` write persists exactly the `content.steps` it was given —
 * so "the steps we sent" and "the steps the server would echo back" are
 * identical by construction. Return shape is UNCHANGED from before this
 * rewrite; both call sites (`SequencesStep.tsx`, `SequenceCopyPane.tsx`) still
 * read `{ newSequenceId, save }` the same way.
 *
 * U17 (F6) — the signature itself now takes an optional 4th
 * `generationInputs`, forwarded verbatim to `duplicateSequence`, so "Save a
 * new version" carries the same "Generate with AI" provenance "Update all"
 * already does onto the duplicate's v1 (and its derived `generation_preset`).
 * Both call sites pass their own `pending.generationInputs ?? undefined` —
 * omitted, this behaves exactly as before.
 *
 * U19 (M1) — the optional 5th `expectedSourceVersionId` is forwarded the same
 * way: it is the SOURCE head the caller's `steps` were edited/generated
 * against, and the server refuses the whole duplicate with 409
 * `version_conflict` if that head has moved (the guard "Update all" already
 * had, which "Save a new version" lacked — Codex round-2 "Might Break" #1: a
 * second tab rebinds the source's Gong flow and saves its own steps, and the
 * fork would otherwise pair the NEW binding with this caller's stale copy).
 * Callers that omit it get the previous behavior — fork from whatever the live
 * head is. Because this helper synthesizes its `save` return value from the
 * duplicate response rather than a second request, a 409 here means NOTHING
 * was created and the caller's `await` rejects; there is no partial state to
 * unwind.
 *
 * The ORIGINAL sequence is never written to by this function: its head is
 * left exactly as it was before the call, which is the whole point of "Save
 * as new copy" — every other rung/playbook still mapping the original keeps
 * seeing the original's saved copy, untouched.
 *
 * What happens to whatever previously pointed at the ORIGINAL sequence (a
 * rung's mapping, in the play-flow editor; nothing at all, on the Library
 * page) is caller-specific and deliberately NOT this helper's job — see
 * `SequencesStep.tsx`'s `handleSaveAsNewCopy` (remaps the current rung onto
 * the duplicate, via the same `SequenceMapping`-spread mechanism
 * `SequenceMappingDrawer.tsx`'s `handleSave` uses) vs. `SequenceCopyPane.tsx`'s
 * (no rung concept exists on the Library page at all — see that file's own
 * module doc for what it does instead: the pane keeps editing the duplicate
 * for the rest of its mounted lifetime).
 */
export async function duplicateSequenceWithEditedSteps(
  apiFetch: ApiFetch,
  sourceSequenceId: string,
  steps: SequenceSteps,
  generationInputs?: PlaybooksGenerationInputs,
  expectedSourceVersionId?: string,
): Promise<{ newSequenceId: string; save: PlaybooksSaveSequenceStepsResponse }> {
  const duplicated = await duplicateSequence(
    apiFetch,
    sourceSequenceId,
    steps,
    generationInputs,
    expectedSourceVersionId,
  );
  return {
    newSequenceId: duplicated.id,
    save: {
      id: duplicated.id,
      versionId: duplicated.versionId,
      versionNumber: duplicated.versionNumber,
      steps,
    },
  };
}
